import { queryOptions } from '@tanstack/react-query'

import type { components } from '@/api/schema'

import { QUOTE_BUILDER_ROOT, QuoteBuilderApiError, type QuoteBuilderApi } from '../api/client'
import { retainNewerStageRead } from '../api/queries'
import type { Quote } from '../api/types'

type Schemas = components['schemas']

export type TaxWorkspace = Schemas['QbTaxWorkspace']
export type TaxSettings = Schemas['QbTaxSettings']
export type TaxEvidence = Schemas['QbTaxEvidenceRead']
export type TaxLine = Schemas['QbTaxLineBreakdown']
export type TaxJurisdiction = Schemas['QbTaxJurisdiction']

// Demo query key: ['quote-taxes', id].
export const taxesQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['quote-taxes', quoteId],
    queryFn: ({ signal }) => api.request<TaxWorkspace>(`/quotes/${quoteId}/taxes`, { signal }),
    structuralSharing: retainNewerStageRead,
  })

/** A certificate whose upload or reading failed; `step` says where to resume (demo `TaxEvidenceError`). */
export class TaxEvidenceError extends Error {
  evidence: TaxEvidence
  step: 'upload' | 'parse'
  canRetry: boolean

  constructor(evidence: TaxEvidence, step: 'upload' | 'parse', canRetry = true) {
    super(
      step === 'parse'
        ? 'Certificate uploaded, but it could not be read. Retry reading.'
        : canRetry
          ? 'Certificate upload failed. Retry upload.'
          : 'Upload failed. Choose the certificate again.',
    )
    this.evidence = evidence
    this.step = step
    this.canRetry = canRetry
  }
}

// crypto.subtle exists only in secure contexts; plain-HTTP origins (local and preview hosts) use this.
function sha256Fallback(input: Uint8Array) {
  const constants = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ])
  const state = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19])
  const paddedLength = Math.ceil((input.length + 9) / 64) * 64
  const padded = new Uint8Array(paddedLength)
  padded.set(input)
  padded[input.length] = 0x80
  new DataView(padded.buffer).setUint32(paddedLength - 4, input.length * 8)
  const words = new Uint32Array(64)
  const rotateRight = (value: number, amount: number) => (value >>> amount) | (value << (32 - amount))
  for (let offset = 0; offset < padded.length; offset += 64) {
    const view = new DataView(padded.buffer, offset, 64)
    for (let index = 0; index < 16; index += 1) words[index] = view.getUint32(index * 4)
    for (let index = 16; index < 64; index += 1) {
      const first = words[index - 15] ?? 0
      const second = words[index - 2] ?? 0
      const sigma0 = rotateRight(first, 7) ^ rotateRight(first, 18) ^ (first >>> 3)
      const sigma1 = rotateRight(second, 17) ^ rotateRight(second, 19) ^ (second >>> 10)
      words[index] = ((words[index - 16] ?? 0) + sigma0 + (words[index - 7] ?? 0) + sigma1) >>> 0
    }
    const working = Array.from(state)
    for (let index = 0; index < 64; index += 1) {
      const [a = 0, b = 0, c = 0, , e = 0, f = 0, g = 0, h = 0] = working
      const sigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)
      const choice = (e & f) ^ (~e & g)
      const temporary1 = (h + sigma1 + choice + (constants[index] ?? 0) + (words[index] ?? 0)) >>> 0
      const sigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22)
      const majority = (a & b) ^ (a & c) ^ (b & c)
      working.unshift((temporary1 + ((sigma0 + majority) >>> 0)) >>> 0)
      working[4] = ((working[4] ?? 0) + temporary1) >>> 0
      working.pop()
    }
    for (let index = 0; index < state.length; index += 1) state[index] = ((state[index] ?? 0) + (working[index] ?? 0)) >>> 0
  }
  return Array.from(state, (word) => word.toString(16).padStart(8, '0')).join('')
}

export async function sha256(file: File) {
  const input = new Uint8Array(await file.arrayBuffer())
  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', input)
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  return sha256Fallback(input)
}

function settingsBody(revision: number, settings: TaxSettings, taxOverride?: string | null) {
  return JSON.stringify({
    expected_revision: revision,
    equipment_taxable: settings.equipment_taxable,
    shipping_taxable: settings.shipping_taxable,
    services_taxable: settings.services_taxable,
    other_taxable: settings.other_taxable,
    line_overrides: settings.line_overrides ?? {},
    // Sent only when Sales changes the tax amount, so taxability saves keep an imported amount.
    ...(taxOverride !== undefined ? { tax_override: taxOverride } : {}),
  })
}

export function taxesApi(api: QuoteBuilderApi) {
  const read = (quoteId: string, signal?: AbortSignal) =>
    api.request<TaxWorkspace>(`/quotes/${quoteId}/taxes`, { signal })
  return {
    read,
    update: (quoteId: string, revision: number, settings: TaxSettings) =>
      api.request<Quote>(`/quotes/${quoteId}/taxes`, { method: 'PATCH', body: settingsBody(revision, settings) }),
    /** Charge this tax amount instead of the calculation ($0 included); ``null`` charges the calculated tax. */
    setOverride: (quoteId: string, revision: number, settings: TaxSettings, taxOverride: string | null) =>
      api.request<Quote>(`/quotes/${quoteId}/taxes`, {
        method: 'PATCH',
        body: settingsBody(revision, settings, taxOverride),
      }),
    applyEvidence: (quoteId: string, revision: number, evidenceId: string, settings: TaxSettings) =>
      api.request<Quote>(`/quotes/${quoteId}/tax-evidence/${evidenceId}/apply`, {
        method: 'POST',
        body: settingsBody(revision, settings),
      }),
    /** Create, upload and read a certificate; resumes from a failed step when given one. */
    createEvidence: async (quoteId: string, file: File, resume?: TaxEvidenceError): Promise<TaxEvidence> => {
      const evidence =
        resume?.evidence ??
        (await api.request<TaxEvidence>(`/quotes/${quoteId}/tax-evidence`, {
          method: 'POST',
          body: JSON.stringify({
            original_file_name: file.name,
            content_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
            sha256: await sha256(file),
          }),
        }))
      let step = resume?.step ?? 'upload'
      let canRetry = resume?.canRetry ?? true
      try {
        if (step === 'upload') {
          if (!evidence.upload_url || !canRetry) {
            canRetry = false
            throw new Error('Upload link unavailable')
          }
          const upload = await api.fetch(evidence.upload_url.replace(QUOTE_BUILDER_ROOT, ''), {
            method: 'PUT',
            headers: evidence.upload_headers ?? {},
            body: file,
          })
          if (!upload.ok) {
            canRetry = upload.status !== 409 && upload.status !== 415
            throw new Error('Upload failed')
          }
          step = 'parse'
        }
        try {
          return await api.request<TaxEvidence>(`/quotes/${quoteId}/tax-evidence/${evidence.id}/parse`, { method: 'POST' })
        } catch (reason) {
          // A lost response can leave the certificate already read on the server.
          if (reason instanceof QuoteBuilderApiError && reason.code === 'tax_evidence_already_parsed') {
            const existing = (await read(quoteId)).evidence.find((item) => item.id === evidence.id)
            if (existing?.status === 'parsed') return existing
          }
          throw reason
        }
      } catch {
        throw new TaxEvidenceError(evidence, step, canRetry)
      }
    },
    /** The stored certificate file, to open in a new tab. */
    download: async (quoteId: string, evidenceId: string) => {
      const response = await api.fetch(`/quotes/${quoteId}/tax-evidence/${evidenceId}/file`)
      if (!response.ok) throw new QuoteBuilderApiError(response.status, {})
      return response.blob()
    },
  }
}
